import { useContext, useEffect, useRef, useState } from "react";
import { InventoryEditProductFormContext } from "../../../Context/InventoryEditProductFormContext";
import { useOutsideClickForTwoRef } from "../../../Hooks/useOutsideClickForTwoRef";
import { categoryToProductsMap, productCategories } from "../../../Routes/InventoryManagement/CategoryData";
import { showToast } from "../../../Components/Toaster/Toaster";
import ChevronDown from '../../../Assets/ChevronDownLargeDarkMode.svg';
import SearchIcon from '../../../Assets/OrderManagement/SearchIcon.svg';

function SelectCategory() {
    const { 
        selectedCategory,
        setSelectedCategory,
        selectedProductType,
        setSelectedProductType,
        productsList,
        setProductsList
    } = useContext(InventoryEditProductFormContext);

    const [categorySearchTerm, setCategorySearchTerm] = useState('');
    const [productSearchTerm, setProductSearchTerm] = useState('');

    const dropdownButtonRef = useRef(null);
    const dropdownListRef = useRef(null);
    const categorySearchInputRef = useRef(null);

    const productDropdownButtonRef = useRef(null);
    const productDropdownListRef = useRef(null);
    const productSearchInputRef = useRef(null);

    useOutsideClickForTwoRef(dropdownButtonRef, dropdownListRef, () => {
        setDropdownOpen(false);
        setCategorySearchTerm(''); // Clear search when closing
    });

    useOutsideClickForTwoRef(productDropdownButtonRef, productDropdownListRef, () => {
        setProductDropdownOpen(false);
        setProductSearchTerm(''); // Clear search when closing
    });

    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [productDropdownOpen, setProductDropdownOpen] = useState(false);

    const [buttonAvailable, setButtonAvailable] = useState(false);

    // Filtered lists based on search terms
    const filteredCategories = productCategories.filter((cat) =>
        cat.category.toLowerCase().includes(categorySearchTerm.toLowerCase())
    );

    const filteredProducts = productsList.filter((product) =>
        product.product.toLowerCase().includes(productSearchTerm.toLowerCase())
    );

    const handleDropdownButton = () => {
        setDropdownOpen((s) => {
            const next = !s;
            if (next) {
                // focus input after opening
                setTimeout(() => {
                    categorySearchInputRef.current?.focus();
                }, 100);
            }
            return next;
        });
        // clear search when (re)opening
        setCategorySearchTerm('');
    };

    const handleProductDropdownButton = () => {
        // Guard: don't open product dropdown when no category selected
        if (!selectedCategory) {
            // keep visual disabled state; optional toast for UX
            showToast('Please select category first.', 'warning');
            return;
        }

        setProductDropdownOpen((s) => {
            const next = !s;
            if (next) {
                setTimeout(() => {
                    productSearchInputRef.current?.focus();
                }, 100);
            }
            return next;
        });
        setProductSearchTerm('');
    };

    const handleSelectCategory = (selectedCategory, e) => {
        e.stopPropagation();
        setSelectedCategory(selectedCategory);
        setDropdownOpen(false);
        setCategorySearchTerm('');
        setProductsList(categoryToProductsMap[selectedCategory] ?? []);
        setSelectedProductType('');
    };

    const handleSelectProductType = (product, e) => {
        e.stopPropagation();
        setSelectedProductType(product);
        setProductDropdownOpen(false);
        setProductSearchTerm('');
    };

    const handleCategorySearchChange = (e) => {
        setCategorySearchTerm(e.target.value);
    };

    const handleProductSearchChange = (e) => {
        setProductSearchTerm(e.target.value);
    };

    const handleSearchInputClick = (e) => {
        e.stopPropagation();
    };

    useEffect(() => {
        if (categoryToProductsMap.hasOwnProperty(selectedCategory)) {
            setProductsList(categoryToProductsMap[selectedCategory]);
            setSelectedProductType('');
        } else {
            setProductsList([]);
            setSelectedProductType('');
        }
    }, [selectedCategory]);

    return (
        <div className="border border-[#EAEAEA] p-5 rounded-xl flex flex-col gap-4">
            <h1 className="font-semibold text-[18px] leading-[26px] select-none">Product Category</h1>

            <div className="flex flex-col gap-3">
                <h1 className="font-medium text-base text-[#121212]">Select category</h1>

                <div
                    onClick={handleDropdownButton}
                    ref={dropdownButtonRef}
                    role="button"
                    aria-haspopup="listbox"
                    aria-expanded={dropdownOpen}
                    className={`relative cursor-pointer flex items-center justify-between gap-[10px] px-3 py-4 bg-[#F5F5F5] border border-[#EAEAEA] rounded-[9px] ${
                        dropdownOpen ? 'border-primaryColor' : ''
                    }`}
                >
                    <p className="font-normal w-[calc(100%-24px)] text-base text-[#666666] select-none line-clamp-1">
                        {selectedCategory ? selectedCategory : 'select category'}
                    </p>

                    <div className="w-6 h-6 flex items-center justify-center select-none">
                        <img src={ChevronDown} alt="toggle" />
                    </div>

                    {dropdownOpen && (
                        <div
                            ref={dropdownListRef}
                            className="bg-[#FFFFFF] max-h-56 overflow-y-auto absolute z-10 top-[58px] left-0 w-full rounded-[9px] shadow-[0px_3px_6px_0px_#0000001F]"
                        >
                            <div
                                className="px-3 py-4 flex items-center gap-[10px] w-full border-b border-[#EAEAEA] sticky top-0 bg-white"
                                onClick={handleSearchInputClick}
                            >
                                <div className="w-6 h-6 flex items-center justify-center">
                                    <img src={SearchIcon} alt="search" />
                                </div>

                                <input
                                    ref={categorySearchInputRef}
                                    type="text"
                                    aria-label="Search category"
                                    className="flex-1 outline-none"
                                    placeholder="Search category"
                                    value={categorySearchTerm}
                                    onChange={handleCategorySearchChange}
                                    onClick={handleSearchInputClick}
                                />
                            </div>

                            {filteredCategories.length > 0 ? (
                                filteredCategories.map((categoryItem) => {
                                    return (
                                        <div
                                            key={categoryItem.id}
                                            role="option" 
                                            aria-selected
                                            tabIndex={0}
                                            onClick={(e) => handleSelectCategory(categoryItem.category, e)}
                                            className="px-3 py-4 font-medium text-base text-[#666666] select-none hover:bg-[#F5F5F5] cursor-pointer"
                                        >
                                            {categoryItem.category}
                                        </div>
                                    );
                                })
                            ) : (
                                <div className="px-3 py-4 font-medium text-base text-[#999999] select-none text-center">
                                    No categories found
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            <div className="flex flex-col gap-3">
                <h1 className="font-medium text-base text-[#121212]">Select product type</h1>

                <div
                    onClick={handleProductDropdownButton}
                    ref={productDropdownButtonRef}
                    role="button"
                    aria-haspopup="listbox"
                    aria-expanded={productDropdownOpen}
                    className={`relative cursor-pointer flex items-center justify-between gap-[10px] px-3 py-4 bg-[#F5F5F5] border border-[#EAEAEA] rounded-[9px] ${
                        productDropdownOpen ? 'border-primaryColor' : ''
                    } ${!selectedCategory ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                    <p className="font-normal w-[calc(100%-24px)] text-base text-[#666666] select-none line-clamp-1">
                        {selectedProductType ? selectedProductType : 'select product type'}
                    </p>

                    <div className="w-6 h-6 flex items-center justify-center select-none">
                        <img src={ChevronDown} alt="toggle" />
                    </div>

                    {productDropdownOpen && selectedCategory && (
                        <div
                            ref={productDropdownListRef}
                            className="bg-[#FFFFFF] max-h-56 overflow-y-auto absolute z-10 top-[58px] left-0 w-full rounded-[9px] shadow-[0px_3px_6px_0px_#0000001F]"
                        >
                            <div
                                className="px-3 py-4 flex items-center gap-[10px] w-full border-b border-[#EAEAEA] sticky top-0 bg-white"
                                onClick={handleSearchInputClick}
                            >
                                <div className="w-6 h-6 flex items-center justify-center">
                                    <img src={SearchIcon} alt="search" />
                                </div>

                                <input
                                    ref={productSearchInputRef}
                                    type="text"
                                    aria-label="Search product type"
                                    className="flex-1 outline-none"
                                    placeholder="Search product type"
                                    value={productSearchTerm}
                                    onChange={handleProductSearchChange}
                                    onClick={handleSearchInputClick}
                                />
                            </div>

                            {filteredProducts.length > 0 ? (
                                filteredProducts.map((product) => {
                                    return (
                                        <div
                                            key={product.id}
                                            role="option" 
                                            aria-selected
                                            tabIndex={0}
                                            onClick={(e) => handleSelectProductType(product.product, e)}
                                            className="px-3 py-4 font-medium text-base text-[#666666] select-none hover:bg-[#F5F5F5] cursor-pointer"
                                        >
                                            {product.product}
                                        </div>
                                    );
                                })
                            ) : (
                                <div className="px-3 py-4 font-medium text-base text-[#999999] select-none text-center">
                                    No products found
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

export default SelectCategory;
